import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { allUnitsGrouped } from '@releve/shared/units';

export interface UnitSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Form-friendly unit picker. Sibling to `UnitCellEditor` (grid cell editor)
 * but wired as a standard Radix `<Select>` — matching the existing form pattern
 * used in EmissionFactorDialog and CreateSustainabilityActivityDialog. Populated from
 * `allUnitsGrouped()` in `src/lib/unitConversion.ts`.
 */
export function UnitSelect({
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  ariaLabel,
}: UnitSelectProps) {
  const { t } = useTranslation();
  const groups = allUnitsGrouped();
  const resolvedPlaceholder = placeholder ?? t('things.form.placeholders.selectUnit');

  return (
    <Select value={value ?? ''} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className} aria-label={ariaLabel}>
        <SelectValue placeholder={resolvedPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        {groups.map((group) => (
          <SelectGroup key={group.code}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.units.map((u) => (
              <SelectItem key={u.code} value={u.code}>
                {u.label} <span className="text-muted-foreground">({u.code})</span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
